/**
 * Per-project loop profile and autonomy (viko.project.yaml `loopProfile` / `autonomy`), saved as
 * `PATCH /api/projects/:id {config}`. The repo's file stays authoritative on the daemon; the hub
 * copy drives the router (start-run policy) and the Inbox.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Lock } from 'lucide-react'
import { useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { qk } from '../lib/queries.js'
import type { AutonomyLevel, LoopProfile, Project, ProjectConfigLite } from '../lib/types.js'
import {
  AUTONOMY_ACTIONS,
  type AutonomyAction,
  effectivePolicy,
  HARD_ASK,
  LOOP_HELP,
} from '../lib/work.js'
import '../routes/project-views.css'
import { errorText, useToast } from './ui.js'

const LEVEL_HELP: Record<AutonomyLevel, string> = {
  careful: 'Ask before everything the loop profile does not cover.',
  balanced: 'Add tasks, pick machines, start and rotate runs on its own.',
  high: 'Also open lanes and switch agents; still asks for rules and agent tasks.',
}

export function useSaveProjectConfig(project: Project) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  return async (patch: Partial<ProjectConfigLite>) => {
    if (!project.config) return toast('This project has no config on the hub yet.', 'error')
    const next = { ...project.config, ...patch }
    qc.setQueryData<Project[]>(qk.projects, (prev) =>
      prev?.map((p) =>
        p.id === project.id
          ? { ...p, config: next, ...(patch.loopProfile ? { loop: patch.loopProfile } : {}) }
          : p,
      ),
    )
    try {
      await b.api.updateProject(project.id, { config: next })
      toast('Saved')
    } catch (e) {
      toast(errorText(e), 'error')
      void qc.invalidateQueries({ queryKey: qk.projects })
    }
  }
}

export function LoopPicker({ project, compact }: { project: Project; compact?: boolean }) {
  const save = useSaveProjectConfig(project)
  const loop: LoopProfile = project.config?.loopProfile ?? project.loop ?? 'fast'
  return (
    <div className="grid gap-1.5">
      <div
        className="choice"
        role="radiogroup"
        aria-label={`${project.name} loop profile`}
        aria-disabled={!project.config || undefined}
      >
        {(['fast', 'balanced', 'careful'] as const).map((l) => (
          <button
            key={l}
            type="button"
            role="radio"
            aria-checked={loop === l}
            disabled={!project.config}
            onClick={() => void save({ loopProfile: l })}
          >
            {l[0]?.toUpperCase()}
            {l.slice(1)}
          </button>
        ))}
      </div>
      {compact ? null : <p className="m-0 text-[14px] leading-5 text-text-2">{LOOP_HELP[loop]}</p>}
    </div>
  )
}

export function AutonomyEditor({ project }: { project: Project }) {
  const save = useSaveProjectConfig(project)
  const cfg = project.config
  const [open, setOpen] = useState(false)
  const level: AutonomyLevel = cfg?.autonomy?.level ?? 'balanced'
  const overrides = cfg?.autonomy?.overrides ?? {}
  const setLevel = (l: AutonomyLevel) =>
    save({ autonomy: { level: l, overrides: { ...overrides } } })
  const setOverride = (a: AutonomyAction, v: 'auto' | 'ask' | 'default') => {
    const next = { ...overrides }
    if (v === 'default') delete next[a]
    else next[a] = v
    return save({ autonomy: { level, overrides: next } })
  }
  return (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <div className="choice" role="radiogroup" aria-label={`${project.name} autonomy`}>
          {(['careful', 'balanced', 'high'] as const).map((l) => (
            <button
              key={l}
              type="button"
              role="radio"
              aria-checked={level === l}
              disabled={!cfg}
              onClick={() => void setLevel(l)}
            >
              {l[0]?.toUpperCase()}
              {l.slice(1)}
            </button>
          ))}
        </div>
        <p className="m-0 text-[14px] leading-5 text-text-2">{LEVEL_HELP[level]}</p>
      </div>
      <details open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
        <summary className="cursor-pointer text-[14px] font-medium text-text-2">
          What Viko does on its own
        </summary>
        <ul className="m-0 mt-2 grid list-none gap-1 p-0" aria-label="Autonomy per action">
          {AUTONOMY_ACTIONS.map(([a, label]) => {
            const { [a]: _own, ...others } = overrides
            const eff = effectivePolicy(
              { ...(cfg ?? {}), autonomy: { level, overrides: others } },
              a,
            )
            const hard = HARD_ASK.includes(a)
            const ov = overrides[a]
            return (
              <li key={a} className="flex items-center gap-2 text-[14px]">
                <span className="flex-1">{label}</span>
                {hard ? (
                  <span className="flex items-center gap-1 text-[14px] text-text-3">
                    <Lock size={12} aria-hidden="true" /> Always asks
                  </span>
                ) : (
                  <select
                    className="input !min-h-8 !w-auto !py-0.5 text-[14px]"
                    aria-label={label}
                    value={ov ?? 'default'}
                    disabled={!cfg}
                    onChange={(e) =>
                      void setOverride(a, e.target.value as 'auto' | 'ask' | 'default')
                    }
                  >
                    <option value="default">
                      Default ({eff === 'auto' ? 'on its own' : 'asks'})
                    </option>
                    <option value="auto">On its own</option>
                    <option value="ask">Ask me</option>
                  </select>
                )}
              </li>
            )
          })}
        </ul>
      </details>
    </div>
  )
}

export function HistoryConsent({ project }: { project: Project }) {
  const save = useSaveProjectConfig(project)
  const history = project.config?.history
  return (
    <section className="pv-sec" aria-labelledby="ps-history">
      <h3 id="ps-history" className="pv-h">
        History and terminal sessions
      </h3>
      <p role="note" className="pv-note">
        Off by default. Enabling shares this project's Claude and Codex conversations, tool output,
        file paths and code with Viko. Secret detection can miss secrets. Only sessions started in
        the last 30 days are included by default. Each machine must also opt in locally: set{' '}
        <code>history.enabled</code> and <code>history.projects["{project.id}"].import</code> in
        viko's <code>config.json</code>. Turning this off stops future uploads; it does not delete
        previously uploaded content.
      </p>
      <label className="pv-check">
        <input
          type="checkbox"
          disabled={!project.config}
          checked={history?.import === true}
          onChange={(e) => void save({ history: { import: e.target.checked, adopt: false } })}
        />
        I consent to upload this project's recent history
      </label>
      <label className="pv-check">
        <input
          type="checkbox"
          disabled={!history?.import}
          checked={history?.adopt === true}
          onChange={(e) => void save({ history: { import: true, adopt: e.target.checked } })}
        />
        I also consent to share live terminal sessions in this project
      </label>
    </section>
  )
}
